<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import axios from "axios";
import { authHeader } from "../../../helpers/auth-header";

import ConfirmationModal from "@/components/modals/confirmation-modal";
import MessageModal from "@/components/modals/message-modal";

import InputGroup from "@/components/forms/input-group";
import SaveButton from "@/components/buttons/save-button";

/**
 * Orders Component
 */
export default {
    components: {
        Layout,
        PageHeader,
        ConfirmationModal,
        MessageModal,
        InputGroup,
        SaveButton
    },
    data() {
        const id = this.$route.params.id;
        const modeName = id === 'add' ? 'Add' : 'Edit';
        return {
            id: id,
            title: `Role`,
            modeName,
            items: [
                {
                    text: "Role"
                },
                {
                    active: true,
                },
            ],

            data: {
                name: null,
                display_name: null,
                description: null,
                permissions: {}
            },

            keys: [
                'admin', 'product', 'order', 'staff', 'branch', 
                'role', 'category', 'timeslot-category', 'report',
            ],

            errors: {},
        };
    },
    computed: {

    },
    async mounted() {
        for (const key of this.keys) {
            const permissionKeys = [
                `view-${key}`,
                `create-${key}`,
                `edit-${key}`,
                `delete-${key}`,
                `export-${key}`
            ];

            for (const permissionKey of permissionKeys) {
                this.$set(this.data.permissions, permissionKey, false);
            }
        }

        if (this.modeName !== 'Add') {
            const result = await axios.get(`${process.env.VUE_APP_APIURL}/setting/role/${this.id}`, {
                headers: authHeader(),
            });
            if (result && result.data['code'] === 'success') {
                const role_data = result.data['data']['role']['role'];
                for (const [key, item] of Object.entries(role_data)) {
                    this.data[key] = item;
                }

                for (const permissionKey in this.data.permissions) {
                    if (result.data['data']['role']['permissions'].includes(permissionKey)) {
                        this.data.permissions[permissionKey] = true;
                    }
                }
            }
        }
    },
    methods: {
        async save() {
            this.errors = {};

            let result;
            const filteredPairs = Object.entries(this.data);
            const requestData = Object.fromEntries(filteredPairs);
            if (this.modeName === 'Add') {
                result = await axios.post(`${process.env.VUE_APP_APIURL}/setting/role`, requestData, {
                    headers: authHeader(),
                });
            } else {
                result = await axios.put(`${process.env.VUE_APP_APIURL}/setting/role/${this.id}`, requestData, {
                    headers: authHeader(),
                });
            }

            if (result) {
                if (result.data['code'] === 'success') {
                    if (this.modeName === 'Add') {
                        this.$router.back();
                    } else {
                        this.$refs.messageModal.showModal('Your record has been updated successfully');
                    }
                } else if (result.data['code'] === 'invalid_field') {
                    this.errors = result.data['errors'];
                }
            }
        },

        processedName(key) {
            const processed = key.replace(/-/g, ' ');
            return processed.charAt(0).toUpperCase() + processed.slice(1);
        },

        hasPermission(permissionKey) {
            return this.role.permissions.some((permission) => permission.id === this.permissions[permissionKey].id);
        },
    }
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <ConfirmationModal ref="confirmationModal" />
        <MessageModal ref="messageModal" />
        <div class="card p-3">
            <div class="row">
                <div class="col-12 col-md-6 col-lg-4">
                    <InputGroup type="text" id="role" displayName="Role*" v-model="data.name" :error="errors.name" />
                </div>
                <div class="col-12 col-md-6 col-lg-4">
                    <InputGroup type="text" id="name" displayName="Name*" v-model="data.display_name" :error="errors.display_name" class="mb-1" />
                </div>
                <div class="col-12 col-md-6 col-lg-4">
                    <InputGroup type="text" id="description" displayName="Description" v-model="data.description" :error="errors.description" class="mb-1" />
                </div>
            </div>

            <div class="row">
                <div class="col-12">
                    <h3 class="headings-color mt-5">Set Permissions</h3>
                    <div class="table-responsive">
                        <table class="table table-bordered text-center">
                            <tr>
                                <th class="bg-primary">Resources</th>
                                <th class="bg-info"><i class="fa fa-eye"></i> &nbsp;View</th>
                                <th class="bg-success"><i class="fa fa-plus"></i> &nbsp;Add</th>
                                <th class="bg-warning"><i class="fa fa-edit"></i> &nbsp;Edit</th>
                                <th class="bg-danger"><i class="fa fa-trash"></i> &nbsp;Delete</th>
                                <th class="bg-secondary"><i class="fa fa-print"></i> &nbsp;Export</th>
                            </tr>
                            <tr v-for="key in keys" :key="key">                            
                                <td>
                                    <label>{{ processedName(key) }}</label>
                                </td>
                                <td>
                                    <input
                                    type="checkbox"
                                    :name="'view-'+key"
                                    v-model="data.permissions[`view-${key}`]"
                                    />
                                </td>
                                <td v-if="!['report', 'profile', 'log', 'general-setting'].includes(key)">
                                    <input
                                    type="checkbox"
                                    v-model="data.permissions[`create-${key}`]"
                                    />
                                </td>
                                <td v-else></td>
                                <td v-if="!['report', 'log'].includes(key)">
                                    <input
                                    type="checkbox"
                                    v-model="data.permissions[`edit-${key}`]"
                                    />
                                </td>
                                <td v-else></td>
                                <td v-if="!['report', 'profile', 'general-setting'].includes(key)">
                                    <input
                                    type="checkbox"
                                    v-model="data.permissions[`delete-${key}`]"
                                    />
                                </td>
                                <td v-else></td>
                                <td v-if="['report'].includes(key)">
                                    <input
                                    type="checkbox"
                                    v-model="data.permissions[`export-${key}`]"
                                    />
                                </td>
                                <td v-else></td>
                            </tr>
                        </table>
                    </div>
                </div>
            </div>

            <div class="row mt-5">
                <div class="col-12">
                    <SaveButton @click="save" />
                </div>
            </div>
        </div>
    </Layout>
</template>